import useCustomList from "@/hooks/useCustomList";
import SText from "@/styles/SText";
import { SaleReportHisItem, SaleReportHisItemAdd } from "@/types/sales";
import { App, Button, Flex, Modal, ModalProps, Table, TableProps } from "antd";
import dayjs from "dayjs";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import DataForm from "./DataForm";
import useDataAdd from "@/hooks/useDataAdd";
import useDataUpdate from "@/hooks/useDataUpdate";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import api from "@/apis/order";
import useDataDel from "@/hooks/useDataDel";
import { dateFormat } from "@/utils/format";

interface DataListModalProps extends ModalProps {
  id: string;
  list: Array<SaleReportHisItem>;
  handleCancel: (props: Array<SaleReportHisItem>) => void;
}

const DataListModal: FC<DataListModalProps> = ({ id, list: defaultList, handleCancel, ...modalProps }) => {

  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [list, , { addItem, updateItem, rmItem }] = useCustomList({ defaultList });
  const { loading: addLoading, add } = useDataAdd<[string, SaleReportHisItemAdd], SaleReportHisItem>({
    api: api.itemAddProState,
  });
  const { onConfirm } = useFrontDelTip({
    cb: (id: string) => onDel(id),
  });
  const { update: updateHis, loading: updateLoading } = useDataUpdate<[string, SaleReportHisItemAdd]>({
    api: api.itemUpdateProState,
  });
  const { del } = useDataDel({ api: api.itemDelProState });

  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [detail, setDetail] = useState<SaleReportHisItem | null>(null);


  const hisColumns: TableProps<SaleReportHisItem>['columns'] = [
    {
      title: <SText text={t("sale.time")} />,
      key: "date",
      render: (_, item) => item.date,
    },
    {
      title: <SText text="SMT" />,
      key: "reportT1",
      render: (_, item) => getHisColumn(item, 't1'),
    },
    {
      title: <SText text="DIP" />,
      key: "reportT2",
      render: (_, item) => getHisColumn(item, 't2')
    },
    {
      title: <SText text="TEST1" />,
      key: "reportT3",
      render: (_, item) => getHisColumn(item, 't3')
    },
    {
      title: <SText text="ASSY" />,
      key: "reportT4",
      render: (_, item) => getHisColumn(item, 't4')
    },
    {
      title: <SText text="TEST2" />,
      key: "reportT5",
      render: (_, item) => getHisColumn(item, 't5')
    },
    {
      title: <SText text="PACK" />,
      key: "reportT6",
      render: (_, item) => getHisColumn(item, 't6')
    },
    {
      title: <SText text={t("sale.inStock")} />,
      key: "reportT7",
      render: (_, item) => getHisColumn(item, 't7')
    },
    {
      title: <SText text={t("sale.outPro")} />,
      key: "reportT8",
      render: (_, item) => getHisColumn(item, 't8')
    },
    {
      title: <SText text={t("common.opt")} />,
      key: "opt",
      render: (val, item) => (
        <Flex>
          <Button type='link' onClick={() => setDetail(item)}>{t('common.edit')}</Button>
          <Button type='link' danger onClick={() => onConfirm(item.id)}>{t('common.del')}</Button>
        </Flex>
      )
    },
  ]

  const getHisColumn = (item: SaleReportHisItem, key: keyof SaleReportHisItem) => {
    return <SText text={(item[key] as number).toString()} />
  }

  const onDel: (id: string) => Promise<void> = (id) => {
    return del(id)
      .then(() => {
        rmItem(id);
        notification.success({ placement: 'top', message: t("common.rmSuccess") });
      })
      .catch(() => {
        notification.error({ placement: 'top', message: t("common.rmError") });
      });
  };

  return (
    <Modal
      title={t('sale.history')}
      closable
      footer={null}
      width={800}
      onCancel={() => {
        handleCancel(list)
      }}
      {...modalProps}
    >
      <Button type='primary' onClick={() => setAddOpen(true)} style={{ marginBottom: 10 }}>{t('sale.addEdit')}</Button>
      <Table
        bordered
        dataSource={list}
        columns={hisColumns}
        scroll={{ x: "max-content" }}
        size="small"
      />
      <DataForm
        key={'addHis'}
        open={addOpen}
        confirmLoading={addLoading}
        handleCancel={() => setAddOpen(false)}
        handleOk={(data) => {
          return new Promise((resolve, reject) => {
            add(id, { ...data, date: dateFormat(dayjs(data.date)) })
              .then((res) => {
                if (list.find(item => item.date === res.date)) {
                  updateItem(res)
                } else {
                  addItem(res);
                }
                setAddOpen(false);
                notification.success({ placement: 'top', message: t('common.saveOk') })
                resolve();
              })
              .catch((err) => {
                const map: {
                  [key in number]: string;
                } = {
                  10010: t('common.networkError'),
                  71010: t('sale.exitsData')
                };
                notification.error({ placement: 'top',
                  message: t('common.addError'),
                  description: map[err] ?? t('common.serverError'),
                });
              });
          });
        }}
        leftTitle={t('common.add')}
        data={null}
      />
      <DataForm
        key={detail?.id}
        open={!!detail}
        confirmLoading={updateLoading}
        handleCancel={() => setDetail(null)}
        handleOk={(data) => {
          return new Promise((resolve, reject) => {
            if (detail) {
              updateHis(detail.id, { ...data, date: detail.date })
                .then(() => {
                  updateItem({ ...detail, ...data });
                  setDetail(null);
                  notification.success({ placement: 'top', message: t('sale.updateSuccess') })
                  resolve();
                })
                .catch((err) => {
                  const map: {
                    [key in number]: string;
                  } = {
                    10010: t('common.networkError'),
                  };
                  notification.error({ placement: 'top',
                    message: t('common.saveFAil'),
                    description: map[err.code] ?? t('common.serverError'),
                  });
                  reject();
                });
            }
          });
        }}
        leftTitle={t('common.edit')}
        data={detail}
      />
    </Modal>
  );
};

export default DataListModal;
